import { useState, useMemo } from "react";
import type {
  AustralianState,
  PropertyType,
  PurchaseType,
  StampDutyInputs,
  StampDutyResults,
  StampDutyTier,
} from "../_types/calculator.types";
import { getStateConfig } from "../_data/stampDutyRates";

/**
 * Default calculator inputs
 */
const defaultInputs: StampDutyInputs = {
  state: "NSW",
  propertyValue: 500000,
  propertyType: "primary",
  purchaseType: "established",
  isFirstHomeBuyer: false,
  isPensioner: false,
  totalIncome: 80000,
  dependentChildren: 0,
};

/**
 * Calculate stamp duty based on tiered rates
 */
function calculateTieredDuty(value: number, tiers: StampDutyTier[]): number {
  for (const tier of tiers) {
    if (value >= tier.min && (tier.max === null || value <= tier.max)) {
      const excess = value - tier.min;
      return tier.base + excess * tier.rate;
    }
  }
  return 0;
}

/**
 * Calculate First Home Buyer stamp duty concession
 */
function calculateFHBConcession(
  state: AustralianState,
  propertyValue: number,
  purchaseType: PurchaseType,
  fullStampDuty: number
): number {
  const config = getStateConfig(state);
  const { exemptionThreshold, concessionThreshold, maxConcession } = config.fhbConcession;

  // Different states have different FHB rules
  switch (state) {
    case "NSW":
      // NSW: Full exemption up to $800k for new homes, partial to $1M
      if (purchaseType === "new" || purchaseType === "vacant") {
        if (propertyValue <= exemptionThreshold) {
          return fullStampDuty;
        }
        if (propertyValue <= concessionThreshold) {
          const ratio = (concessionThreshold - propertyValue) / (concessionThreshold - exemptionThreshold);
          return fullStampDuty * ratio;
        }
      }
      // Established homes: different thresholds
      if (purchaseType === "established") {
        if (propertyValue <= 650000) {
          return fullStampDuty;
        }
        if (propertyValue <= 800000) {
          const ratio = (800000 - propertyValue) / 150000;
          return fullStampDuty * ratio;
        }
      }
      return 0;

    case "VIC":
      // VIC: Full exemption up to $600k, partial to $750k
      if (propertyValue <= exemptionThreshold) {
        return fullStampDuty;
      }
      if (propertyValue <= concessionThreshold) {
        const ratio = (concessionThreshold - propertyValue) / (concessionThreshold - exemptionThreshold);
        return fullStampDuty * ratio;
      }
      return 0;

    case "QLD":
      // QLD: First Home Concession for homes up to $700k, partial to $800k
      if (propertyValue <= exemptionThreshold) {
        return fullStampDuty;
      }
      if (propertyValue <= concessionThreshold) {
        const ratio = (concessionThreshold - propertyValue) / (concessionThreshold - exemptionThreshold);
        return fullStampDuty * ratio;
      }
      return 0;

    case "WA":
      // WA: Full exemption up to $430k, tapered to $530k
      if (propertyValue <= exemptionThreshold) {
        return fullStampDuty;
      }
      if (propertyValue <= concessionThreshold) {
        const ratio = (concessionThreshold - propertyValue) / (concessionThreshold - exemptionThreshold);
        return fullStampDuty * ratio;
      }
      return 0;

    case "SA":
      // SA: Full exemption up to $650k for eligible homes
      if (propertyValue <= exemptionThreshold) {
        return fullStampDuty;
      }
      if (propertyValue <= concessionThreshold) {
        const ratio = (concessionThreshold - propertyValue) / (concessionThreshold - exemptionThreshold);
        return fullStampDuty * ratio;
      }
      return 0;

    case "TAS":
      // TAS: 50% discount capped at $7000
      const tasDiscount = Math.min(fullStampDuty * 0.5, maxConcession || 7000);
      if (propertyValue <= exemptionThreshold) {
        return tasDiscount;
      }
      return 0;

    case "ACT":
      // ACT: Income-tested concessions
      if (propertyValue <= exemptionThreshold) {
        // Simplified - ACT has complex income-based concessions
        return fullStampDuty * 0.5;
      }
      return 0;

    case "NT":
      // NT: Already has significant stamp duty relief built into rates
      if (propertyValue <= exemptionThreshold) {
        return fullStampDuty;
      }
      return 0;

    default:
      return 0;
  }
}

/**
 * Calculate First Home Owner Grant eligibility
 */
function calculateFHOG(
  state: AustralianState,
  propertyValue: number,
  purchaseType: PurchaseType
): number {
  const config = getStateConfig(state);
  const { amount, newHomeOnly, maxPropertyValue } = config.fhog;

  // FHOG generally only applies to new homes
  if (newHomeOnly && purchaseType === "established") {
    return 0;
  }

  // Check property value threshold
  if (propertyValue > maxPropertyValue) {
    return 0;
  }

  return amount;
}

/**
 * Calculate pensioner concession
 */
function calculatePensionerConcession(
  state: AustralianState,
  propertyValue: number,
  propertyType: PropertyType,
  fullStampDuty: number
): number {
  const config = getStateConfig(state);

  // Must be primary residence
  if (propertyType !== "primary") {
    return 0;
  }

  if (!config.pensionerConcession) {
    return 0;
  }

  const { maxPropertyValue, concessionPercent } = config.pensionerConcession;

  if (propertyValue <= maxPropertyValue) {
    return fullStampDuty * concessionPercent;
  }

  return 0;
}

/**
 * Calculate transfer fee
 */
function calculateTransferFee(state: AustralianState, propertyValue: number): number {
  const config = getStateConfig(state);
  const { transferFeeBase, transferFeePerThousand } = config;

  return transferFeeBase + (propertyValue / 1000) * transferFeePerThousand;
}

/**
 * Hook for stamp duty calculations
 */
export function useStampDutyCalculator() {
  const [inputs, setInputs] = useState<StampDutyInputs>(defaultInputs);

  const results = useMemo<StampDutyResults>(() => {
    const config = getStateConfig(inputs.state);

    // Calculate base stamp duty
    const rates = inputs.propertyType === "investment" && config.investmentRates
      ? config.investmentRates
      : config.rates;

    let stampDuty = calculateTieredDuty(inputs.propertyValue, rates);

    // Calculate FHB concession if applicable
    let fhbConcession = 0;
    if (inputs.isFirstHomeBuyer && inputs.propertyType === "primary") {
      fhbConcession = calculateFHBConcession(
        inputs.state,
        inputs.propertyValue,
        inputs.purchaseType,
        stampDuty
      );
    }

    // Calculate pensioner concession if applicable
    let pensionerConcession = 0;
    if (inputs.isPensioner && !inputs.isFirstHomeBuyer) {
      pensionerConcession = calculatePensionerConcession(
        inputs.state,
        inputs.propertyValue,
        inputs.propertyType,
        stampDuty
      );
    }

    // Apply concessions to stamp duty
    stampDuty = Math.max(0, stampDuty - fhbConcession - pensionerConcession);

    // Calculate FHOG
    const fhog = inputs.isFirstHomeBuyer && inputs.propertyType === "primary"
      ? calculateFHOG(inputs.state, inputs.propertyValue, inputs.purchaseType)
      : 0;

    // Fixed fees
    const mortgageRegistration = config.mortgageRegistrationFee;
    const transferFee = calculateTransferFee(inputs.state, inputs.propertyValue);

    // Totals
    const totalGovernmentFees = stampDuty + mortgageRegistration + transferFee;
    const totalGovernmentGrant = fhog;
    const netCost = totalGovernmentFees - totalGovernmentGrant;

    return {
      stampDuty: Math.round(stampDuty * 100) / 100,
      mortgageRegistration: Math.round(mortgageRegistration * 100) / 100,
      transferFee: Math.round(transferFee * 100) / 100,
      totalGovernmentFees: Math.round(totalGovernmentFees * 100) / 100,
      fhog,
      fhbConcession: Math.round(fhbConcession * 100) / 100,
      pensionerConcession: Math.round(pensionerConcession * 100) / 100,
      totalGovernmentGrant,
      netCost: Math.round(netCost * 100) / 100,
    };
  }, [inputs]);

  const updateInput = <K extends keyof StampDutyInputs>(
    key: K,
    value: StampDutyInputs[K]
  ) => {
    setInputs((prev) => ({ ...prev, [key]: value }));
  };

  const resetInputs = () => {
    setInputs(defaultInputs);
  };

  return {
    inputs,
    results,
    updateInput,
    setInputs,
    resetInputs,
  };
}
